/**
 * ทะเบียนเครื่องสแกน Android (Handheld Device Registry) — `/v1/devices` (D-074, D-079, D-176)
 *
 * ADMIN เท่านั้น (`DEVICE_REGISTRY` เป็นโมดูล System Administration, D-060) ไม่มีเส้นทางลบ — revoke เป็นการเปลี่ยนสถานะ
 * `device_key` เป็น public key/opaque token ที่เครื่องสร้าง ไม่ใช่ความลับ แต่เซิร์ฟเวอร์ไม่ส่งกลับมาในทุกคำตอบ
 */

import { callJson, type StockFailure, type StockRequestOptions } from '../inventory/stock';

export const DEVICES_PATH = '/v1/devices';

export interface RegisteredDevice {
  readonly device_id: string;
  readonly plant_code: string;
  readonly work_center_code: string | null;
  readonly status: string;
  readonly registered_at: string;
}

export interface RegisterDeviceBody {
  readonly plant_code: string;
  readonly work_center_code?: string;
  readonly device_key: string;
}

export type DeviceListResult =
  | { readonly status: 'ready'; readonly items: readonly RegisteredDevice[] }
  | StockFailure;
export type DeviceResult = { readonly status: 'ready'; readonly device: RegisteredDevice } | StockFailure;

function s(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

export function parseDevice(raw: unknown): RegisteredDevice | null {
  if (typeof raw !== 'object' || raw === null) {
    return null;
  }
  const r = raw as Record<string, unknown>;
  const id = s(r.device_id);
  const plant = s(r.plant_code);
  const status = s(r.status);
  const at = s(r.registered_at);
  const line = r.work_center_code === null || r.work_center_code === undefined ? null : s(r.work_center_code);
  if (!id || !plant || !status || !at || (line === null && typeof r.work_center_code === 'string')) {
    return null;
  }
  return { device_id: id, plant_code: plant, work_center_code: line, status, registered_at: at };
}

export async function fetchDevices(
  filters: { readonly status?: string; readonly plant_code?: string } = {},
  options: StockRequestOptions = {},
): Promise<DeviceListResult> {
  const params = new URLSearchParams();
  if (filters.status) {
    params.append('status', filters.status);
  }
  if (filters.plant_code?.trim()) {
    params.append('plant_code', filters.plant_code.trim());
  }
  const query = params.toString();
  const result = await callJson('GET', `${DEVICES_PATH}${query === '' ? '' : `?${query}`}`, undefined, {}, options);
  if (result.status !== 'ok') {
    return result;
  }
  const body = result.payload as { items?: unknown } | null;
  if (typeof body !== 'object' || body === null || !Array.isArray(body.items)) {
    return { status: 'invalid-response' };
  }
  const items: RegisteredDevice[] = [];
  for (const entry of body.items) {
    const device = parseDevice(entry);
    if (device === null) {
      return { status: 'invalid-response' };
    }
    items.push(device);
  }
  return { status: 'ready', items };
}

export async function registerDevice(
  body: RegisterDeviceBody,
  options: StockRequestOptions = {},
): Promise<DeviceResult> {
  const result = await callJson(
    'POST',
    `${DEVICES_PATH}/register`,
    {
      plant_code: body.plant_code,
      device_key: body.device_key,
      ...(body.work_center_code ? { work_center_code: body.work_center_code } : {}),
    },
    {},
    options,
  );
  if (result.status !== 'ok') {
    return result;
  }
  const device = parseDevice(result.payload);
  return device === null ? { status: 'invalid-response' } : { status: 'ready', device };
}

/** `POST /v1/devices/{id}/revoke` — ไม่มี body; เครื่องที่ revoke แล้วซิงก์ไม่ได้อีก (แถวไม่ถูกลบ) */
export async function revokeDevice(deviceId: string, options: StockRequestOptions = {}): Promise<DeviceResult> {
  const result = await callJson('POST', `${DEVICES_PATH}/${encodeURIComponent(deviceId)}/revoke`, undefined, {}, options);
  if (result.status !== 'ok') {
    return result;
  }
  const device = parseDevice(result.payload);
  return device === null ? { status: 'invalid-response' } : { status: 'ready', device };
}
